import { useMemo } from 'react'

/**
 * Identity-owned route paths. Host applications (axilon-devops, ontology-ui)
 * mount these routes via the identity feature lazy imports; the strings here
 * must match the segments declared in each host's routing.
 *
 * If you change a value here, also update the matching value in the host app's
 * full paths config (e.g. `domains/axilon-devops/src/shared/core/config/paths.ts`).
 */
export const identityPaths = {
  authentication: {
    login: '/auth/login',
    accountRecovery: '/auth/account-recovery'
  },
  onboarding: {
    changeInitialPassword: '/onboarding/change-initial-password',
    enableMFA: '/onboarding/enable-mfa'
  },
  accountSettings: {
    profile: '/account-settings',
    changePassword: '/account-settings/change-password',
    mfa: '/account-settings/mfa'
  },
  users: '/users',
  // Default landing page after login. Hosts can override via setHomePath().
  home: '/'
}

let _homePath: string = identityPaths.home

/**
 * Configure where to redirect after a successful login. Defaults to `/`.
 * Host apps that have a different landing page should call this at boot.
 */
export const setIdentityHomePath = (path: string): void => {
  _homePath = path
}

export const getIdentityHomePath = (): string => _homePath

/**
 * Identity-side `usePaths()` substitute. Returns identity-owned route paths.
 * Memoized so consumers get a referentially stable object (guards depend on it
 * in useEffect deps).
 */
export const useIdentityPaths = () => {
  const homePath = _homePath
  return useMemo(
    () => ({
      authentication: identityPaths.authentication,
      onboarding: identityPaths.onboarding,
      accountSettings: identityPaths.accountSettings,
      users: identityPaths.users,
      app: { home: homePath, users: identityPaths.users }
    }),
    [homePath]
  )
}
